import { useBookingTrips } from '@/hooks/useBookingTrips';
import type { BookedTripDTO, BookingDTO } from '@/types/dto/agent';
import { isTimestampInPast } from '@/utils/dateTime';
import { useCallback, useMemo } from 'react';

export const useBookingRefundEligibleTrips = (booking: BookingDTO | null) => {
  const { outboundTrips: allOutboundTrips, inboundTrips: allInboundTrips } =
    useBookingTrips(booking);

  const refundEligibleTrips = useCallback((trips: Array<BookedTripDTO>) => {
    return trips.filter(
      (trip) =>
        trip.legs?.every(
          (leg) =>
            leg.departureTime &&
            !isTimestampInPast(leg.departureTime, 'Europe/Stockholm') // TODO: Get timezone dynamically
        ) &&
        trip.bookedOffers?.some((offer) =>
          offer.admissions?.some(
            ({ status, refundable }) =>
              status !== 'REFUNDED' &&
              // EXCHANGED admissions were superseded by a change-booking and
              // moved off this trip; a wholly-exchanged leftover trip must not
              // be offered as a refund target.
              status !== 'EXCHANGED' &&
              refundable === 'YES'
          )
        )
    );
  }, []);

  const refundEligibleOutboundTrips = useMemo(
    () => refundEligibleTrips(allOutboundTrips),
    [refundEligibleTrips, allOutboundTrips]
  );
  const refundEligibleInboundTrips = useMemo(
    () => refundEligibleTrips(allInboundTrips),
    [refundEligibleTrips, allInboundTrips]
  );

  return { refundEligibleInboundTrips, refundEligibleOutboundTrips };
};
